/**
 * Connect WhatsApp Page — the single surface where a clinic picks between:
 *
 *   1. Business App (manual deep-link sending)
 *   2. Business API (automated messaging via Meta / Gupshup / other BSP)
 *   3. Viyora Demo (trial-only shared number)
 *
 * The chosen mode is enforced server-side by `whatsapp.resolve_provider()`;
 * this page just gives admins a friendly UI to configure it and shows the
 * live connection status (number, provider, verification, messaging).
 */
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { apiError } from "@/lib/apiError";
import {
  MessageCircle, Smartphone, Zap, Sparkles, CheckCircle2, XCircle,
  Loader2, ArrowRight, Shield, AlertCircle, Info,
} from "lucide-react";
import { useAuth } from "@/lib/auth";
import MetaEmbeddedSignupButton from "@/components/MetaEmbeddedSignupButton";

const MODES = [
  {
    key: "app",
    title: "WhatsApp Business App",
    subtitle: "Manual · No Meta, no API",
    icon: Smartphone,
    accent: "from-[#0F9D58]/10 to-white",
    highlight: "#0F9D58",
    perks: [
      "No Meta Business Account or API keys needed",
      "Connect your existing Business App number",
      "One-tap manual send from every action",
      "Great for solo practices starting today",
    ],
    limitations: [
      "No automated reminders or bulk messaging",
      "Templates & AI receptionist not available",
    ],
  },
  {
    key: "api",
    title: "WhatsApp Business API",
    subtitle: "Automated · Recommended",
    icon: Zap,
    accent: "from-clay/10 to-white",
    highlight: "#D06A42",
    perks: [
      "Fully automated reminders, invoices, prescriptions",
      "Approved-template management inside Viyora",
      "Bulk messaging & AI Receptionist enabled",
      "Meta Cloud, Gupshup, Twilio, WATI, Interakt, AiSensy, 360dialog …",
    ],
    limitations: [
      "Requires Meta Business Account / BSP credentials",
    ],
  },
  {
    key: "demo",
    title: "Use Viyora Demo WhatsApp",
    subtitle: "Trial only · Shared number",
    icon: Sparkles,
    accent: "from-pine/5 to-white",
    highlight: "#4A6B5D",
    perks: [
      "Test every WhatsApp feature immediately",
      "Zero setup — messages go through Viyora's number",
      "Ideal to explore automation before upgrading",
    ],
    limitations: [
      "Patients see \"Viyora\" as the sender, not your clinic",
      "Automatically disables when the trial ends",
    ],
  },
];

const API_PROVIDERS_HELP = {
  meta: {
    label: "Meta Cloud API (Recommended)",
    docs: "https://developers.facebook.com/docs/whatsapp/cloud-api",
    fields: ["access_token", "phone_number_id", "display_phone", "waba_id"],
  },
  gupshup: { label: "Gupshup", docs: "https://www.gupshup.io/", fields: ["api_key", "source_number"] },
  twilio:  { label: "Twilio",  docs: "https://www.twilio.com/whatsapp", fields: ["api_key", "source_number"] },
  wati:    { label: "WATI",    docs: "https://www.wati.io/",            fields: ["api_key", "source_number"] },
  interakt:{ label: "Interakt",docs: "https://www.interakt.shop/",       fields: ["api_key", "source_number"] },
  aisensy: { label: "AiSensy", docs: "https://www.aisensy.com/",          fields: ["api_key", "source_number"] },
  d360:    { label: "360dialog",docs:"https://www.360dialog.com/",       fields: ["api_key", "source_number"] },
  msg91:   { label: "MSG91",   docs: "https://msg91.com/whatsapp",       fields: ["api_key", "source_number"] },
  infobip: { label: "Infobip", docs: "https://www.infobip.com/",         fields: ["api_key", "source_number"] },
  kaleyra: { label: "Kaleyra", docs: "https://www.kaleyra.com/",         fields: ["api_key", "source_number"] },
  yellowai:{ label: "Yellow.ai",docs:"https://www.yellow.ai/",           fields: ["api_key", "source_number"] },
};

export default function ConnectWhatsApp() {
  const { user } = useAuth();
  const [conn, setConn] = useState(null);
  const [selected, setSelected] = useState("api");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [appPhone, setAppPhone] = useState("");
  const [apiForm, setApiForm] = useState({
    provider: "meta", access_token: "", phone_number_id: "",
    display_phone: "", waba_id: "", api_key: "", source_number: "",
  });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/whatsapp/connection");
      setConn(data);
      if (data.mode && data.mode !== "none") setSelected(data.mode);
      if (data.app?.phone_number) setAppPhone(data.app.phone_number);
      if (data.provider) setApiForm((f) => ({ ...f, provider: data.provider }));
    } catch (e) {
      toast.error("Could not load WhatsApp connection");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const isAdmin = user?.role === "admin";

  const connectApp = async () => {
    if (!appPhone) return toast.error("Enter your WhatsApp Business phone number");
    setSaving(true);
    try {
      const { data } = await api.post("/whatsapp/connection/app", { mode: "app", phone_number: appPhone });
      setConn(data);
      toast.success("Business App connected — manual send is ready");
    } catch (e) {
      toast.error(apiError(e, "Failed to connect"));
    } finally { setSaving(false); }
  };

  const connectApi = async () => {
    setSaving(true);
    try {
      const payload = { mode: "api", provider: apiForm.provider, ...apiForm };
      const { data } = await api.post("/whatsapp/connection/api", payload);
      setConn(data);
      toast.success(`${API_PROVIDERS_HELP[apiForm.provider]?.label || apiForm.provider} connected`);
    } catch (e) {
      toast.error(apiError(e, "Failed to connect"));
    } finally { setSaving(false); }
  };

  const connectDemo = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/whatsapp/connection/demo", { mode: "demo" });
      setConn(data);
      toast.success("Viyora Demo WhatsApp enabled");
    } catch (e) {
      toast.error(apiError(e, "Cannot enable demo"));
    } finally { setSaving(false); }
  };

  const disconnect = async () => {
    if (!window.confirm("Disconnect WhatsApp for this clinic?")) return;
    setSaving(true);
    try {
      const { data } = await api.delete("/whatsapp/connection");
      setConn(data);
      toast.success("Disconnected");
    } catch (e) {
      toast.error(apiError(e, "Failed"));
    } finally { setSaving(false); }
  };

  if (loading) {
    return (
      <div className="p-8 grid place-items-center min-h-[60vh]">
        <Loader2 className="w-6 h-6 animate-spin text-ink-soft" />
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto p-6 sm:p-8 space-y-6" data-testid="connect-whatsapp-page">
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 rounded-2xl bg-[#0F9D58]/10 text-[#0F9D58] grid place-items-center shrink-0">
          <MessageCircle className="w-6 h-6" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-widest text-ink-soft">Messaging · WhatsApp</div>
          <h1 className="font-display text-3xl leading-tight">Connect WhatsApp</h1>
          <p className="text-sm text-ink-mid mt-1">
            Choose how your clinic sends WhatsApp messages. You can switch modes anytime.
          </p>
        </div>
      </div>

      <ConnectionStatusCard conn={conn} onDisconnect={disconnect} disabled={!isAdmin || saving} />

      {/* --- Meta Embedded Signup hero (Phase C) -------------------------- */}
      {isAdmin && conn?.mode !== "api" && (
        <div className="rounded-3xl bg-gradient-to-br from-[#1A362B] to-[#254B3D] text-white p-6 sm:p-7 relative overflow-hidden" data-testid="wa-embedded-signup-hero">
          <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full bg-white/5 blur-2xl pointer-events-none" />
          <div className="relative flex flex-col sm:flex-row items-start sm:items-center gap-6 justify-between">
            <div>
              <div className="inline-flex items-center gap-2 text-[11px] font-medium bg-white/10 border border-white/20 rounded-full px-3 py-1">
                <span className="w-1.5 h-1.5 rounded-full bg-[#25D366]" />
                Recommended · One-click connect
              </div>
              <div className="mt-3 font-display text-2xl leading-tight">
                Connect your WhatsApp Business in 60 seconds
              </div>
              <p className="mt-2 text-sm text-white/70 max-w-lg leading-relaxed">
                Powered by Meta Embedded Signup. Approve access from Meta's popup and
                Viyora will fetch your WABA, phone number and quality rating automatically —
                no Phone Number ID or Access Token to copy-paste.
              </p>
            </div>
            <MetaEmbeddedSignupButton onConnected={() => { toast.success("WhatsApp linked"); load(); }} />
          </div>
        </div>
      )}
      {/* ---------------------------------------------------------------- */}


      {!isAdmin && (
        <div className="rounded-2xl bg-clay/10 border border-clay/30 p-3 text-sm text-clay flex items-start gap-2">
          <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
          Only clinic admins can change the WhatsApp connection. Ask an admin to update the mode.
        </div>
      )}

      {/* Mode selector */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {MODES.map((m) => {
          const Icon = m.icon;
          const on = selected === m.key;
          const isCurrent = conn?.mode === m.key;
          return (
            <button
              key={m.key}
              onClick={() => setSelected(m.key)}
              disabled={!isAdmin}
              data-testid={`mode-card-${m.key}`}
              className={`text-left rounded-2xl border p-4 transition-all bg-gradient-to-br ${m.accent}
                          ${on ? "border-pine shadow-sm ring-1 ring-pine/20" : "border-[#E8EAE9] hover:border-pine/30"}
                          disabled:opacity-60 disabled:cursor-not-allowed`}>
              <div className="flex items-start justify-between gap-2 mb-3">
                <div className="w-10 h-10 rounded-xl grid place-items-center"
                     style={{ background: `${m.highlight}20`, color: m.highlight }}>
                  <Icon className="w-5 h-5" />
                </div>
                {isCurrent && (
                  <span className="text-[9px] uppercase tracking-widest font-semibold text-ink bg-pine/10 rounded-full px-2 py-0.5">
                    Current
                  </span>
                )}
              </div>
              <div className="font-display text-lg leading-tight">{m.title}</div>
              <div className="text-[11px] uppercase tracking-widest mt-0.5" style={{ color: m.highlight }}>{m.subtitle}</div>
              <ul className="mt-3 space-y-1">
                {m.perks.map((p) => (
                  <li key={p} className="text-xs text-ink-mid flex gap-1.5">
                    <CheckCircle2 className="w-3.5 h-3.5 shrink-0 mt-0.5" style={{ color: m.highlight }} />
                    <span>{p}</span>
                  </li>
                ))}
                {m.limitations.map((p) => (
                  <li key={p} className="text-xs text-ink-soft flex gap-1.5">
                    <XCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                    <span>{p}</span>
                  </li>
                ))}
              </ul>
            </button>
          );
        })}
      </div>

      {/* Config forms per mode */}
      {selected === "app" && (
        <AppConfigForm
          value={appPhone} onChange={setAppPhone}
          onConnect={connectApp} disabled={!isAdmin} saving={saving}
          isCurrent={conn?.mode === "app"} />
      )}

      {selected === "api" && (
        <ApiConfigForm
          value={apiForm} onChange={setApiForm}
          onConnect={connectApi} disabled={!isAdmin} saving={saving}
          isCurrent={conn?.mode === "api"} />
      )}

      {selected === "demo" && (
        <DemoConfigCard
          onConnect={connectDemo} disabled={!isAdmin} saving={saving}
          isCurrent={conn?.mode === "demo"} />
      )}

      {/* Feature comparison */}
      <FeatureMatrix conn={conn} />
    </div>
  );
}

/* ============================================================================
 *  Connection status card
 * ==========================================================================*/
function ConnectionStatusCard({ conn, onDisconnect, disabled }) {
  const mode = conn?.mode || "none";
  const kind = conn?.kind;
  const badge = mode === "app" ? { text: "Manual", tone: "app" }
              : mode === "api" ? { text: "Automated", tone: "api" }
              : mode === "demo" ? { text: "Demo", tone: "demo" }
              : { text: "Not connected", tone: "none" };
  const badgeStyle = {
    app:  { bg: "#0F9D5820", fg: "#0F9D58" },
    api:  { bg: "#D06A4220", fg: "#8A3F1E" },
    demo: { bg: "#4A6B5D20", fg: "#264029" },
    none: { bg: "#5D686330", fg: "#5D6863" },
  }[badge.tone];

  return (
    <div className="rounded-2xl border border-[#E8EAE9] bg-white p-4 flex flex-wrap items-center gap-4"
         data-testid="whatsapp-connection-status">
      <div className="w-12 h-12 rounded-full grid place-items-center shrink-0"
           style={{ background: badgeStyle.bg, color: badgeStyle.fg }}>
        <MessageCircle className="w-6 h-6" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="font-display text-lg">{conn?.ui_status || "Not connected"}</div>
          <span className="text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded-full"
                style={{ background: badgeStyle.bg, color: badgeStyle.fg }}>
            {badge.text}
          </span>
        </div>
        <div className="text-xs text-ink-mid mt-1 flex flex-wrap gap-x-3 gap-y-0.5">
          {conn?.connected_number && <span>Number: <b className="text-ink">{conn.connected_number}</b></span>}
          {conn?.provider && <span>Provider: <b className="text-ink">{conn.provider}</b></span>}
          {conn?.verification_status && <span>Verification: <b className="text-ink">{conn.verification_status}</b></span>}
          <span>Messaging: <b className="text-ink">{conn?.messaging_status || "—"}</b></span>
        </div>
      </div>
      {mode !== "none" && (
        <button
          onClick={onDisconnect}
          disabled={disabled}
          data-testid="whatsapp-disconnect"
          className="text-xs px-3 py-1.5 rounded-full text-clay border border-clay/40 hover:bg-clay/10 disabled:opacity-40">
          Disconnect
        </button>
      )}
    </div>
  );
}

/* ============================================================================
 *  Mode-specific config forms
 * ==========================================================================*/
function AppConfigForm({ value, onChange, onConnect, disabled, saving, isCurrent }) {
  return (
    <div className="rounded-2xl border border-[#E8EAE9] bg-white p-5 space-y-3" data-testid="app-config-form">
      <div>
        <div className="font-display text-lg">Connect your Business App number</div>
        <div className="text-xs text-ink-soft mt-0.5">
          Include country code, e.g. <code>+91 98104 53892</code>. Viyora will open WhatsApp with each message pre-filled — you just tap Send.
        </div>
      </div>
      <div className="flex flex-wrap gap-2 items-center">
        <input
          type="tel" value={value} onChange={(e) => onChange(e.target.value)}
          placeholder="+91 98104 53892" disabled={disabled}
          data-testid="app-phone-input"
          className="flex-1 min-w-[200px] rounded-xl border border-[#E8EAE9] px-4 py-2.5 text-sm bg-white" />
        <button
          onClick={onConnect} disabled={disabled || saving}
          data-testid="btn-connect-app"
          className="text-sm px-4 py-2.5 rounded-full bg-[#0F9D58] text-linen hover:brightness-110 inline-flex items-center gap-2 disabled:opacity-60">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Smartphone className="w-4 h-4" />}
          {isCurrent ? "Update number" : "Connect Business App"}
        </button>
      </div>
      <div className="rounded-xl bg-clay/10 border border-clay/30 p-3 text-[11px] text-clay flex gap-2 items-start">
        <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />
        <span>
          <b>Manual Mode</b> — Automation is not available with WhatsApp Business App.
          Upgrade to WhatsApp API for automatic reminders, templates, and bulk messaging.
        </span>
      </div>
    </div>
  );
}

function ApiConfigForm({ value, onChange, onConnect, disabled, saving, isCurrent }) {
  const help = API_PROVIDERS_HELP[value.provider] || API_PROVIDERS_HELP.meta;
  const set = (k, v) => onChange({ ...value, [k]: v });
  return (
    <div className="rounded-2xl border border-[#E8EAE9] bg-white p-5 space-y-4" data-testid="api-config-form">
      <div>
        <div className="font-display text-lg">Business API credentials</div>
        <div className="text-xs text-ink-soft mt-0.5">
          Pick your BSP.  Only <b>Meta Cloud API</b> and <b>Gupshup</b> are fully wired today — other providers save your keys but will queue as mock until Phase B adapters land.
        </div>
      </div>
      <div>
        <label className="text-[10px] uppercase tracking-widest text-ink-soft">Provider</label>
        <select
          value={value.provider} onChange={(e) => set("provider", e.target.value)}
          disabled={disabled}
          data-testid="api-provider-select"
          className="mt-1 w-full rounded-xl border border-[#E8EAE9] px-3 py-2 text-sm bg-white">
          {Object.entries(API_PROVIDERS_HELP).map(([k, v]) => (
            <option key={k} value={k}>{v.label}</option>
          ))}
        </select>
      </div>

      {value.provider === "meta" ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Access Token" val={value.access_token} set={(v) => set("access_token", v)} disabled={disabled} secret testid="api-access-token" />
          <Field label="Phone Number ID" val={value.phone_number_id} set={(v) => set("phone_number_id", v)} disabled={disabled} testid="api-phone-number-id" />
          <Field label="Display Phone" val={value.display_phone} set={(v) => set("display_phone", v)} disabled={disabled} placeholder="+91 98…" testid="api-display-phone" />
          <Field label="WABA ID (optional)" val={value.waba_id} set={(v) => set("waba_id", v)} disabled={disabled} testid="api-waba-id" />
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="API Key" val={value.api_key} set={(v) => set("api_key", v)} disabled={disabled} secret testid="api-key" />
          <Field label="Source Number" val={value.source_number} set={(v) => set("source_number", v)} disabled={disabled} placeholder="+91 98…" testid="api-source-number" />
        </div>
      )}

      <div className="flex items-center justify-between gap-3 flex-wrap">
        <a href={help.docs} target="_blank" rel="noreferrer"
           className="text-xs text-ink-mid hover:text-ink underline decoration-dotted">
          {help.label} · Setup docs ↗
        </a>
        <button
          onClick={onConnect} disabled={disabled || saving}
          data-testid="btn-connect-api"
          className="text-sm px-4 py-2.5 rounded-full bg-clay text-linen hover:brightness-110 inline-flex items-center gap-2 disabled:opacity-60">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
          {isCurrent ? "Update credentials" : "Connect API"}
        </button>
      </div>
    </div>
  );
}

function Field({ label, val, set, disabled, placeholder, secret, testid }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-widest text-ink-soft">{label}</label>
      <input
        type={secret ? "password" : "text"} value={val || ""} onChange={(e) => set(e.target.value)}
        placeholder={placeholder} disabled={disabled}
        data-testid={testid}
        className="mt-1 w-full rounded-xl border border-[#E8EAE9] px-3 py-2 text-sm bg-white" />
    </div>
  );
}

function DemoConfigCard({ onConnect, disabled, saving, isCurrent }) {
  return (
    <div className="rounded-2xl border border-[#E8EAE9] bg-white p-5 space-y-3" data-testid="demo-config-card">
      <div>
        <div className="font-display text-lg">Try Viyora Demo WhatsApp</div>
        <div className="text-xs text-ink-soft mt-0.5">
          Messages are sent using Viyora's WhatsApp number so patients see <b>Viyora</b> as the sender. Perfect for exploring automation before you connect your own number.
        </div>
      </div>
      <div className="rounded-xl bg-linen/60 border border-[#E8EAE9] p-3 text-xs text-ink-mid flex gap-2 items-start">
        <Shield className="w-4 h-4 mt-0.5 shrink-0 text-ink" />
        <span>Automatically disables when your free trial ends. Connect your Business App or API before then to keep messaging enabled.</span>
      </div>
      <div className="flex justify-end">
        <button
          onClick={onConnect} disabled={disabled || saving}
          data-testid="btn-connect-demo"
          className="text-sm px-4 py-2.5 rounded-full bg-pine text-linen hover:bg-pine/90 inline-flex items-center gap-2 disabled:opacity-60">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
          {isCurrent ? "Demo active" : "Enable demo"}
        </button>
      </div>
    </div>
  );
}

/* ============================================================================
 *  Feature comparison matrix
 * ==========================================================================*/
function FeatureMatrix({ conn }) {
  const features = [
    ["Send prescription",    "send_prescription"],
    ["Send invoice",         "send_invoice"],
    ["Send consent",         "send_consent"],
    ["Send warranty card",   "send_warranty"],
    ["Post-op instructions", "send_instructions"],
    ["Send patient record",  "send_patient_record"],
    ["Send X-rays",          "send_xrays"],
    ["Payment link",         "send_payment_link"],
    ["Appointment confirm.", "appointment_confirmation"],
    ["Automated reminders",  "automation"],
    ["Bulk messaging",       "bulk_messaging"],
    ["Template management",  "template_management"],
    ["AI Receptionist",      "ai_receptionist"],
  ];
  const modes = ["app", "api", "demo"];
  const state = useMemo(() => {
    const build = (m) => ({ app: false, api: true, demo: true, ...m });
    return {
      app:  build({ automation: false, bulk_messaging: false, template_management: false, ai_receptionist: false,
                    send_prescription: true, send_invoice: true, send_consent: true, send_warranty: true,
                    send_instructions: true, send_patient_record: true, send_xrays: true,
                    send_payment_link: true, appointment_confirmation: true }),
      api:  build({}),
      demo: build({ bulk_messaging: false, template_management: false }),
    };
  }, []);

  const activeMode = conn?.mode;

  return (
    <div className="rounded-2xl border border-[#E8EAE9] bg-white overflow-hidden" data-testid="feature-matrix">
      <div className="px-4 py-3 border-b border-[#E8EAE9] flex items-center justify-between">
        <div>
          <div className="font-display text-base">Feature matrix</div>
          <div className="text-[11px] text-ink-soft">What each mode unlocks</div>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-linen/50 text-[10px] uppercase tracking-widest text-ink-soft">
            <tr>
              <th className="text-left px-4 py-2">Feature</th>
              {modes.map((m) => (
                <th key={m} className={`px-4 py-2 text-center min-w-[100px] ${activeMode === m ? "text-ink font-semibold" : ""}`}>
                  {m === "app" ? "Business App" : m === "api" ? "Business API" : "Demo"}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-[#F0F0F0]">
            {features.map(([label, key]) => (
              <tr key={key}>
                <td className="px-4 py-2 text-ink/80">{label}</td>
                {modes.map((m) => (
                  <td key={m} className="px-4 py-2 text-center">
                    {state[m][key]
                      ? <CheckCircle2 className="w-4 h-4 inline text-[#2D6A4F]" />
                      : <XCircle className="w-4 h-4 inline text-ink-disabled" />}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
